import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  GaugeIcon,
  AddressBookIcon,
  HouseLineIcon,
  SunIcon,
  CpuIcon,
  WrenchIcon,
  TicketIcon,
  CalendarIcon,
  CreditCardIcon,
  BellIcon,
  ShieldCheckIcon,
  CertificateIcon,
  ChartBarIcon,
  UsersThreeIcon,
  ClockCounterClockwiseIcon,
  ToolboxIcon,
  ClipboardTextIcon,
  PackageIcon,
  SignOutIcon,
} from "@phosphor-icons/react";

import { useAuth } from "../contexts/AuthContext";
import BrandMark from "./BrandMark";
import RoleBadge from "./RoleBadge";
import GlobalSearch from "./layout/GlobalSearch";
import CommandPalette from "./layout/CommandPalette";

// Sidebar items.
// `live: true`     → real, navigable route.
// `live: false`    → visual-only placeholder for a future module (non-interactive).
// `roles`          → optional whitelist; absent = all authenticated roles.
// `divider: true`  → renders a separator between sections.
const NAV_ITEMS = [
  { key: "dashboard", to: "/", label: "Dashboard", icon: GaugeIcon, live: true },
  { key: "customers", to: "/customers", label: "Customers", icon: AddressBookIcon, live: true, roles: ["owner", "ops", "lead_tech"] },
  { key: "properties", to: "/properties", label: "Properties", icon: HouseLineIcon, live: true },
  { key: "solar-systems", to: "/solar-systems", label: "Solar Systems", icon: SunIcon, live: true },
  { key: "equipment", to: "/equipment", label: "Equipment", icon: CpuIcon, live: true },
  { key: "warranties", to: "/warranties", label: "Warranties", icon: ShieldCheckIcon, live: true },
  { key: "monitoring", to: "/monitoring", label: "Monitoring", icon: BellIcon, live: true },
  { key: "service-requests", to: "/service-requests", label: "Service Requests", icon: TicketIcon, live: true, roles: ["owner", "ops"] },
  { key: "dispatch", to: "/dispatch", label: "Dispatch", icon: CalendarIcon, live: true, roles: ["owner", "ops"] },
  { key: "work-orders", to: "/work-orders", label: "Work Orders", icon: WrenchIcon, live: true },
  { key: "diagnostic-reports", to: "/diagnostic-reports", label: "Diagnostic Reports", icon: ClipboardTextIcon, live: true, roles: ["owner", "ops"] },
  { key: "rma-records", to: "/rma-records", label: "RMA / Warranty", icon: PackageIcon, live: true, roles: ["owner", "ops"] },
  { key: "audit-log", to: "/audit-log", label: "Audit Log", icon: ClockCounterClockwiseIcon, live: true, roles: ["owner"] },
  { key: "my-work", to: "/my-work", label: "My Work", icon: ToolboxIcon, live: true, roles: ["lead_tech", "tech_assistant"] },
  { divider: true, key: "div-billing" },
  { key: "protection-plans", to: "/protection-plans", label: "Protection Plans", icon: CertificateIcon, live: true, roles: ["owner", "ops"] },
  { key: "subscriptions", to: "/subscriptions", label: "Subscriptions", icon: CreditCardIcon, live: true, roles: ["owner", "ops"] },
  { key: "notifications", to: "/notifications", label: "Notifications", icon: BellIcon, live: true, roles: ["owner", "ops"] },
  { divider: true, key: "div-future" },
  { key: "team", to: "/team", label: "Team", icon: UsersThreeIcon, live: true },
  { key: "reports", to: "/reports", label: "Reports", icon: ChartBarIcon, live: true, roles: ["owner"] },
];

export default function Layout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);

  // Cmd/Ctrl+K → toggle command palette globally.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((p) => !p);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!user) return null;

  const canSearch = user.role === "owner" || user.role === "ops";

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex w-60 shrink-0 flex-col bg-slate-900 text-slate-300"
      >
        <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-800">
          <BrandMark size={36} />
          <div>
            <div className="font-heading text-sm font-bold tracking-tight text-white leading-none">
              ZION SOLAR
            </div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-1 font-semibold">
              Service OS
            </div>
          </div>
        </div>

        <nav className="flex-1 py-3" data-testid="sidebar-nav">
          {NAV_ITEMS.map((item) => {
            if (item.divider) {
              return (
                <div
                  key={item.key}
                  className="my-3 mx-5 border-t border-slate-800"
                />
              );
            }
            if (item.roles && !item.roles.includes(user.role)) return null;
            const Icon = item.icon;

            if (item.live) {
              return (
                <NavLink
                  key={item.key}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.key}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-5 py-3 text-sm transition-colors ${
                      isActive
                        ? "bg-slate-800 text-white border-l-[3px] border-amber-500 font-semibold"
                        : "border-l-[3px] border-transparent text-slate-300 hover:bg-slate-800/70 hover:text-white"
                    }`
                  }
                >
                  <Icon size={18} weight="regular" />
                  <span className="flex-1">{item.label}</span>
                </NavLink>
              );
            }

            // Visual placeholder — not a link, not focusable.
            return (
              <div
                key={item.key}
                aria-disabled="true"
                data-testid={`nav-placeholder-${item.key}`}
                className="group flex items-center gap-3 px-5 py-3 text-sm text-slate-500 border-l-[3px] border-transparent select-none cursor-not-allowed"
              >
                <Icon size={18} weight="regular" />
                <span className="flex-1">{item.label}</span>
                <span className="text-[9px] uppercase tracking-wider text-slate-600 font-semibold">
                  soon
                </span>
              </div>
            );
          })}
        </nav>

        <div className="px-5 py-4 border-t border-slate-800">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            v0.3.0 · phase 3
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header
          data-testid="app-header"
          className="h-14 flex items-center justify-between gap-4 px-6 bg-white border-b border-slate-200"
        >
          <div className="min-w-0">
            <div className="font-heading text-sm font-semibold text-slate-900 truncate">
              {title}
            </div>
            {subtitle && (
              <div className="text-[11px] text-slate-500 mt-0.5 truncate font-medium">
                {subtitle}
              </div>
            )}
          </div>
          {/* Global search — owner/ops only (techs see a hint to use Cmd+K) */}
          <div className="hidden lg:flex flex-1 justify-center px-6">
            {canSearch ? (
              <GlobalSearch />
            ) : (
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 text-xs text-slate-500 hover:border-amber-400"
                data-testid="topbar-cmdk"
                title="Open command palette"
              >
                Press
                <kbd className="font-data rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px]">
                  {typeof navigator !== "undefined" &&
                  /Mac/i.test(navigator.platform || "")
                    ? "⌘K"
                    : "Ctrl+K"}
                </kbd>
                to navigate
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {actions}
            <div className="hidden sm:flex items-center gap-2.5 text-right">
              <div>
                <div
                  className="text-sm font-medium text-slate-900 leading-none"
                  data-testid="header-username"
                >
                  {user.name}
                </div>
                <div className="text-[11px] text-slate-500 mt-1 font-medium">
                  {user.email}
                </div>
              </div>
              <RoleBadge role={user.role} />
            </div>
            <button
              data-testid="logout-button"
              onClick={async () => {
                await logout();
                nav("/login");
              }}
              title="Sign out"
              aria-label="Sign out"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
            >
              <SignOutIcon size={16} />
            </button>
          </div>
        </header>
        <main className="flex-1 p-6 overflow-x-hidden animate-fade-in">
          {children}
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
